import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import type { GetBaseViewerDataQuery } from 'generated/graphql';

type WorklistItem = NonNullable<GetBaseViewerDataQuery['worklistItems']>['items'][number];
type StudyPartial = Pick<WorklistItem['studies'][number], 'seriesCount' | 'stackedFrames'>;
type StackUnion = StudyPartial['stackedFrames'][number];
export type RefetchDiffData = Pick<
  GetBaseViewerDataQuery,
  'worklistItems' | 'studiesBySmid' | 'matchHangingProtocolByWorklistsAndStudies'
>;

const getFrameCount = (st: StackUnion): number => ('frames' in st ? (st.frames?.length ?? 0) : 0);

const getStudies = (d: Nullable<RefetchDiffData>): StudyPartial[] => [
  ...(d?.worklistItems?.items.flatMap((i) => [...i.studies, ...(i.autoloadedPriors ?? [])]) ?? []),
  ...(d?.studiesBySmid ?? []),
];

export const logRefetchDiff = (
  afterData: Nullable<RefetchDiffData>,
  beforeData: Nullable<RefetchDiffData>
): void => {
  const studiesBefore = getStudies(beforeData);
  const studiesAfter = getStudies(afterData);
  const studyCountBefore = studiesBefore.length;
  const studyCountAfter = studiesAfter.length;
  const seriesBefore = studiesBefore.reduce((sum, s) => sum + s.seriesCount, 0);
  const seriesAfter = studiesAfter.reduce((sum, s) => sum + s.seriesCount, 0);
  const stacksBefore = studiesBefore.reduce((sum, s) => sum + s.stackedFrames.length, 0);
  const stacksAfter = studiesAfter.reduce((sum, s) => sum + s.stackedFrames.length, 0);
  const framesBefore = studiesBefore.reduce(
    (sum, s) => sum + s.stackedFrames.reduce((ssum, st) => ssum + getFrameCount(st), 0),
    0
  );
  const framesAfter = studiesAfter.reduce(
    (sum, s) => sum + s.stackedFrames.reduce((ssum, st) => ssum + getFrameCount(st), 0),
    0
  );

  // Normalize undefined → null so an absent field and an explicit null are
  // treated identically, and a transition from absent → present (or vice versa)
  // is correctly detected as a change.
  const hpBefore = beforeData?.matchHangingProtocolByWorklistsAndStudies ?? null;
  const hpAfter = afterData?.matchHangingProtocolByWorklistsAndStudies ?? null;
  const hpChanged = hpBefore?.smid !== hpAfter?.smid || hpBefore?.name !== hpAfter?.name;

  if (
    !hpChanged &&
    studyCountBefore === studyCountAfter &&
    seriesBefore === seriesAfter &&
    stacksBefore === stacksAfter &&
    framesBefore === framesAfter
  ) {
    return;
  }

  analytics.track(viewer.sys.precacheRefetchDiff, {
    hpChanged,
    ...(hpChanged && {
      hpBefore: hpBefore ? { smid: hpBefore.smid, name: hpBefore.name } : null,
      hpAfter: hpAfter ? { smid: hpAfter.smid, name: hpAfter.name } : null,
    }),
    studyCountBefore,
    studyCountAfter,
    studyCountDiff: studyCountAfter - studyCountBefore,
    seriesCountBefore: seriesBefore,
    seriesCountAfter: seriesAfter,
    stackCountBefore: stacksBefore,
    stackCountAfter: stacksAfter,
    frameCountBefore: framesBefore,
    frameCountAfter: framesAfter,
  });
};
